// import tokens, { Tokens } from '@airbus/design-tokens'
import tokens from '@airbus/design-tokens'
import getModalValues from './getModalValues'

/* 
This function generates a theme with multiple color modes from our design tokens.

Assumptions:
- interactives, controls & text visual style depends on the background's color 
- a page theme may be hybrid (ex: airbus "corporate" with the blue header) and contain primary (white) and secondary (blue) background color (and tertiary grey)
- even some small components may have background colors that impact the widgets it contains (combobox chips use cool grey because on warm grey)
=> a solution could be to have multiple color modes (white, light, brand for a start), and to have containers provide the adequate mode to its children
- for obvious homogeneity reasons, there will be a lot of similarities between brands themes, and reverse variants for dark backgrounds are largely made of white...
- DS is a WIP, but it'd cool to support more than one brand/page theme, even if it's still experimental. At least it can be an advantage for prototyping
=> we should restrict the number of tokens to the maximum and automatize mode generation until design decisions are made. Tokens can be later promoted from the mode or components code
- all the problems/regretted decisions currently in the UIKit (such as the use of opacity for disabled buttons, or the lack of homogeneity between components colors)
are easier to document/hack-on here instead of in tokens 

Refs:
- https://medium.com/eightshapes-llc/light-dark-9f8ea42c9081
- https://slack.engineering/building-dark-mode-on-desktop-811508b5d15f
- https://medium.com/@kylegach/the-themes-are-always-changing-but-the-tokens-stay-the-same-258d57b18f31
- https://material-ui.com/customization/palette/
- https://material-ui.com/customization/theming/
- https://theme-ui.com/color-modes/
- https://github.com/system-ui/theme-ui/blob/master/packages/color-modes/src/index.js
- https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/styles/createPalette.js
*/

type ModalMode = {
  getModalValues: (token: { onWhite: any; onDark: any }) => string
  type: 'light' | 'dark'
}

type AltModalModes = {
  dark: ModalMode
}

/**
 * FIXME this concept of modes is meant to be removed at some point
 * A lot of the assumptions above are not valid
 * We are mutating the output of design-tokens package here
 * Making it more difficult to find tokens source
 * We should be able to use the "Tokens" type directly instead
 */
// export type ThemeTokens = Omit<Tokens, 'color'> & {
//   color: Omit<Tokens['color'], 'theme'> &
//     ModalMode & {
//       modes: { dark: ModalMode }
//     }
// }
export type ThemeTokens = Omit<any, 'color'> & {
  color: Omit<any['color'], 'theme'> &
    ModalMode & {
      modes: { dark: ModalMode }
    }
}

/**
 * take the tokens and add color modes utilities
 * @param brandName
 * @returns tokens with augmented color modes
 */
export default function createTheme(brandName = 'airbus'): ThemeTokens {
  // if (!tokens[brandName] && process.env.NODE_ENV !== 'production') {
  //   console.error(`[AIRBUS] the brand \`${brandName}\` is not supported.`)
  //   return {} as ThemeTokens
  // }

  const { color, ...otherTokens } = tokens
  const { theme, ...otherColors } = color
  const lightMode: ModalMode = {
    type: 'light',
    getModalValues: (tokens: any) => getModalValues(tokens, 'white'),
  }
  const altModes: AltModalModes = {
    dark: {
      type: 'dark',
      getModalValues: (tokens: any) => getModalValues(tokens, 'dark'),
    },
  }

  return {
    color: {
      ...lightMode,
      ...otherColors,
      modes: altModes,
    },
    ...otherTokens,
  }
}
